<template>
  <div class="content">
    <v-card class="d-flex pa-1 bg" outlined>
      <div class="mr-auto d-flex top-button-group">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="expandAll()">
              <v-icon small>mdi-arrow-expand-vertical</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.unfold') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="collapseAll()">
              <v-icon small>mdi-arrow-collapse-vertical</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.fold') }}</span>
        </v-tooltip>
      </div>
      <div class="top-button-group">
        <BtnAddSystem />
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="save()" :disabled="disabledSave">
              <v-icon small>mdi-content-save</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.save') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="redo()" :disabled="disabledRedo">
              <v-icon small>mdi-redo-variant</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.revert') }}</span>
        </v-tooltip>
      </div>
    </v-card>
    <tree-grid ref="systems" dense :columns="columns" :tree="tree" :empty-text="emptyText">
      <template v-slot:cell.color="{ row }">
        <div class="color-cell" :style="colorCellStyle(row)" />
      </template>
      <template v-slot:cell.actions="{ row }">
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small class="mr-2" @click="edit(row)">mdi-pencil</v-icon>
          </template>
          <span>{{ $t('common.edit') }}</span>
        </v-tooltip>
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small v-if="row.type == 'system'" @click="deleteSystem(row)">mdi-delete</v-icon>
          </template>
          <span>{{ $t('common.delete') }}</span>
        </v-tooltip>
      </template>
    </tree-grid>
    <component v-bind:is="component" :paths="paths" :values="values" v-model="showForm" />
  </div>
</template>

<script>
import bus from '@/utils/bus';
import TreeGrid from '@/components/grid/TreeGrid';
import BtnAddSystem from './BtnAddSystem';
import { api } from '@/api/common/instances';
import { CHANGE_STATUS } from '@/constants';
import { isEqual, get, set, merge } from 'lodash';
import { pascalCase } from 'change-case';

const beforeChangeItems = [];

export default {
  components: {
    TreeGrid,
    BtnAddSystem,
  },
  data() {
    return {
      systems: [],
      tree: [],
      component: null,
      paths: null,
      values: null,
      showForm: false,
      disabledSave: true,
      disabledRedo: true,
      emptyText: this.$t('$vuetify.noDataText'),
      columns: [
        { type: 'prop', prop: 'id', label: this.$t('system.id'), width: '15%' },
        { type: 'prop', prop: 'alias', label: this.$t('system.alias'), width: '25%' },
        { type: 'prop', prop: 'color', label: this.$t('system.color'), width: '10%' },
        { type: 'prop', prop: 'description', label: this.$t('system.description'), width: '40%' },
        { type: 'prop', prop: 'actions', label: '', width: '10%' },
      ],
    };
  },
  computed: {
    classContents() {
      return {
        'contents-background-dark': this.$vuetify.theme.isDark,
      };
    },
  },
  watch: {
    systems: {
      deep: true,
      handler: 'setDisabled',
    },
  },
  methods: {
    colorCellStyle(row) {
      return row.color ? `background-color:${row.color};` : '';
    },
    expandAll() {
      this.tree.filter(item => !item._delete).forEach(item => this.expand(item, true));
    },
    collapseAll() {
      this.tree.filter(item => !item._delete).forEach(item => this.expand(item, false));
    },
    expand(item, flag) {
      this.setTreeValue(item._paths.tree, '_isExpanded', flag);
      this.getTreeChildren(item).forEach(child => {
        this.setTreeValue(child._paths.tree, '_isFolded', !flag);
        this.setTreeValue(child._paths.tree, '_isExpanded', flag);
      });
    },
    getValue(path, defaultValue = null) {
      return get(this.systems, path, defaultValue);
    },
    setValue(path, value) {
      return set(this.systems, path, value);
    },
    getTreeChildren(item) {
      return this.$refs.systems.$refs.treeGridTable.getChildren(item);
    },
    setTreeValue(path, key, value) {
      this.$refs.systems.$refs.treeGridTable.setValue(path, key, value);
      set(this.tree, `${path}.${key}`, value);
    },
    async save() {
      if (await this.$dialog.confirm(this.$t('common.save.all.q'))) {
        this.setSystemStatus();
        const result = await api.put('/preference/systems', this.systems);
        if (result.status == 200) this.retrieve();
      }
    },
    async redo() {
      if (await this.$dialog.confirm(this.$t('common.redo.all.q'))) {
        this.systems = merge([], beforeChangeItems);
        this.tree = this.getTree(this.systems);
      }
    },
    async edit(row) {
      this.paths = row._paths;
      this.values = this.getValue(row._paths.data);
      const form = pascalCase(row.type);
      this.component = (await import(`./${form}Form.vue`)).default;
      this.showForm = true;
    },
    async deleteSystem(row) {
      if (await this.$dialog.confirm(this.$t('common.delete.q'))) {
        this.setValue(`${row._paths.data}.status`, CHANGE_STATUS.DELETED);
        this.setTreeValue(row._paths.tree, '_delete', true);
        this.setTreeValue(row._paths.tree, '_isFolded', true);
        this.expand(row, !true);
      }
    },
    changeStatus(paths, item) {
      const pathItem = get(beforeChangeItems, paths.data);
      const equals = Object.keys(pathItem)
        .filter(key => key != 'status')
        .every(key => isEqual(pathItem[key], item[key]));
      this.setValue(`${paths.data}.status`, equals ? CHANGE_STATUS.NORMAL : CHANGE_STATUS.UPDATED);
    },
    changeData(paths, item) {
      const treeItem = get(this.tree, paths.tree); // path 에 해당하는 tree item
      const treeColumns = this.columns.map(item => item.prop); // 선언된 tree header key 목록
      const treeKeys = Object.keys(treeItem).filter(item => treeColumns.includes(item)); // treeItem key 목록과 treeHeader 와의 교집합
      treeKeys.forEach(key => set(this.tree, `${paths.tree}.${key}`, item[key])); // data 에서 변경 사항을 tree 에 반영
    },
    addEvent() {
      bus.$on('req-system-ids', () => {
        bus.$emit(
          'res-system-ids',
          this.systems.map(s => s.id),
        );
      });
      bus.$on('change-data', (paths, item) => {
        this.changeStatus(paths, item);
        this.changeData(paths, item);
      });
      bus.$on('insert-data', item => {
        this.systems.push(item);
        const convertedTreeItem = this.getTree([item], this.tree.length)[0]; // add data tree last-index
        this.tree.push(convertedTreeItem);
      });
    },
    removeEvent() {
      bus.$off('req-system-ids');
      bus.$off('change-data');
      bus.$off('insert-data');
    },
    setDisabled(systems) {
      this.disabledSave = isEqual(beforeChangeItems, systems);
      this.disabledRedo = this.disabledSave;
    },
    setSystemStatus() {
      const getChildren = row => {
        let children = [];
        const concat = row => {
          const ch = ['abapInstances', 'javaInstances', 'nodes'].reduce((acc, prop) => acc.concat(Object.prototype.hasOwnProperty.call(row, prop) ? row[prop] : []), []);
          children = children.concat(ch);
          ch.forEach(item => concat(item));
        };
        concat(row);
        return children;
      };
      this.systems.forEach(system => {
        const updated = getChildren(system).some(child => child.status == CHANGE_STATUS.UPDATED);
        if (updated) system.status = CHANGE_STATUS.UPDATED;
      });
    },
    getTree(data, seq = 0) {
      const list = [];
      data.forEach((system, systemIdx) => {
        const systemIndex = systemIdx + seq; // add data tree last-index
        list.push({
          id: system.id,
          type: 'system',
          alias: system.alias,
          _delete: false,
          _paths: {
            tree: `${systemIndex}`,
            data: `${systemIndex}`,
          },
        });
        system.abapInstances.forEach((abap, abapIdx) => {
          list.push({
            id: abap.id,
            type: 'abap',
            alias: abap.alias,
            parentId: abap.systemId,
            color: abap.color,
            description: abap.description,
            _delete: false,
            _paths: {
              tree: `${systemIndex}.children.${abapIdx}`,
              data: `${systemIndex}.abapInstances.${abapIdx}`,
            },
          });
        });
        system.javaInstances.forEach((java, javaIdx) => {
          list.push({
            id: java.id,
            type: 'java',
            alias: java.alias,
            parentId: java.systemId,
            description: java.description,
            _delete: false,
            _paths: {
              tree: `${systemIndex}.children.${javaIdx}`,
              data: `${systemIndex}.javaInstances.${javaIdx}`,
            },
          });
          java.nodes.forEach((node, nodeIdx) => {
            list.push({
              id: node.id,
              type: 'node',
              alias: node.alias,
              parentId: node.instanceId,
              color: node.color,
              description: node.description,
              _delete: false,
              _paths: {
                tree: `${systemIndex}.children.${javaIdx}.children.${nodeIdx}`,
                data: `${systemIndex}.javaInstances.${javaIdx}.nodes.${nodeIdx}`,
              },
            });
          });
        });
      });
      const tree = [];
      const childrenOf = {};
      list.forEach(item => {
        const { id: id, parentId: parentId = 0 } = item;
        childrenOf[id] = childrenOf[id] || [];
        item.children = childrenOf[id];
        item._isExpanded = true;
        parentId ? (childrenOf[parentId] = childrenOf[parentId] || []).push(item) : tree.push(item);
      });
      return tree;
    },
    async retrieve() {
      ({ data: this.systems } = await api.get('/preference/systems'));
      this.tree = this.getTree(this.systems);
      merge(beforeChangeItems, this.systems);
    },
  },
  created() {
    this.retrieve();
    this.addEvent();
  },
  destroyed() {
    this.removeEvent();
  },
};
</script>

<style scoped>
.content {
  min-width: 800px;
}

.color-cell {
  width: 20px;
  height: 15px;
}

.top-row {
  margin: 0 0 0 0;
}
</style>
